// ============================================
// Master Formulator — Seed Ingredient Registry
// Ingredients used by the 10 seed products
// ============================================

import type { IngredientRecord } from '../../types/masterFormulator';

export const SEED_INGREDIENT_REGISTRY: Partial<IngredientRecord>[] = [
  // ─────────────────────────────────────────────
  // DHEA
  // ─────────────────────────────────────────────
  {
    ingredientId: 'ING-DHEA',
    commonName: 'DHEA (Dehydroepiandrosterone)',
    scientificName: 'Dehydroepiandrosterone',
    aliases: ['prasterone', 'dehydroisoandrosterone', '3-beta-hydroxyandrost-5-en-17-one'],
    category: 'prohormone',
    fdaStatus: 'gras',
    absoluteContraindications: [
      'hormone_sensitive_cancer (breast, prostate, ovarian, endometrial)',
      'pregnancy',
    ],
    relativeContraindications: [
      'polycystic_ovary_syndrome',
      'endometriosis',
      'uterine_fibroids',
      'liver_disease',
    ],
    pregnancySafe: false,
    lactationSafe: false,
    pediatricSafe: false,
    hepaticCaution: true,
    renalCaution: false,
    evidenceGrade: 'B',
    biomarkerTargets: [
      {
        biomarkerId: 'dhea_s',
        biomarkerName: 'DHEA-Sulfate',
        expectedEffect: 'increase',
        magnitude: 'significant',
        timeframe: '4-8 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'testosterone_total',
        biomarkerName: 'Total Testosterone',
        expectedEffect: 'increase',
        magnitude: 'moderate',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'B',
      },
      {
        biomarkerId: 'estradiol',
        biomarkerName: 'Estradiol (E2)',
        expectedEffect: 'increase',
        magnitude: 'minimal',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'B',
      },
    ],
    knownInteractions: [
      { interactionId: 'INT-DHEA-INSULIN', substanceName: 'Insulin', severity: 'moderate', actionRequired: 'warn_provider' },
      { interactionId: 'INT-DHEA-HORMONE', substanceName: 'Hormone Therapy (estrogen/testosterone)', severity: 'major', actionRequired: 'warn_provider' },
    ],
    tolerableUpperIntake: 50,
    tolerableUpperIntakeUnit: 'mg',
  },

  // ─────────────────────────────────────────────
  // Vitamin D3
  // ─────────────────────────────────────────────
  {
    ingredientId: 'ING-VITD3',
    commonName: 'Vitamin D3',
    scientificName: 'Cholecalciferol',
    aliases: ['cholecalciferol', 'calciol', 'vitamin D3', 'sunshine vitamin'],
    category: 'vitamin',
    fdaStatus: 'gras',
    absoluteContraindications: [
      'hypercalcemia',
      'granulomatous_disease (sarcoidosis)',
    ],
    relativeContraindications: [
      'primary_hyperparathyroidism',
      'lymphoma (some types produce excess calcitriol)',
      'kidney_stones_calcium_oxalate',
    ],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: true,
    evidenceGrade: 'A',
    biomarkerTargets: [
      {
        biomarkerId: 'vitamin_d_25oh',
        biomarkerName: '25-Hydroxyvitamin D',
        expectedEffect: 'increase',
        magnitude: 'significant',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'pth',
        biomarkerName: 'Parathyroid Hormone',
        expectedEffect: 'decrease',
        magnitude: 'moderate',
        timeframe: '12-16 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'calcium_serum',
        biomarkerName: 'Serum Calcium',
        expectedEffect: 'increase',
        magnitude: 'minimal',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
    ],
    knownInteractions: [
      { interactionId: 'INT-VITD3-THIAZIDE', substanceName: 'Thiazide Diuretics', severity: 'moderate', actionRequired: 'warn_provider' },
    ],
    tolerableUpperIntake: 4000,
    tolerableUpperIntakeUnit: 'IU',
  },

  // ─────────────────────────────────────────────
  // EPA (Eicosapentaenoic Acid)
  // ─────────────────────────────────────────────
  {
    ingredientId: 'ING-EPA',
    commonName: 'EPA (Eicosapentaenoic Acid)',
    scientificName: 'Eicosapentaenoic acid',
    aliases: ['EPA', 'icosapentaenoic acid', 'timnodonic acid', '20:5(n-3)'],
    category: 'fatty_acid',
    fdaStatus: 'gras',
    absoluteContraindications: [
      'fish_allergy (if fish-derived)',
    ],
    relativeContraindications: [
      'active_bleeding_disorder',
      'upcoming_surgery_within_2_weeks',
    ],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: false,
    evidenceGrade: 'A',
    biomarkerTargets: [
      {
        biomarkerId: 'triglycerides',
        biomarkerName: 'Triglycerides',
        expectedEffect: 'decrease',
        magnitude: 'significant',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'hs_crp',
        biomarkerName: 'High-Sensitivity C-Reactive Protein',
        expectedEffect: 'decrease',
        magnitude: 'moderate',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'omega3_index',
        biomarkerName: 'Omega-3 Index',
        expectedEffect: 'increase',
        magnitude: 'significant',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
    ],
    knownInteractions: [
      { interactionId: 'INT-OMEGA3-WARFARIN', substanceName: 'Warfarin', severity: 'major', actionRequired: 'block' },
    ],
    tolerableUpperIntake: 3000,
    tolerableUpperIntakeUnit: 'mg',
  },

  // ─────────────────────────────────────────────
  // DHA (Docosahexaenoic Acid)
  // ─────────────────────────────────────────────
  {
    ingredientId: 'ING-DHA',
    commonName: 'DHA (Docosahexaenoic Acid)',
    scientificName: 'Docosahexaenoic acid',
    aliases: ['DHA', 'cervonic acid', '22:6(n-3)'],
    category: 'fatty_acid',
    fdaStatus: 'gras',
    absoluteContraindications: [
      'fish_allergy (if fish-derived)',
    ],
    relativeContraindications: [
      'active_bleeding_disorder',
    ],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: false,
    evidenceGrade: 'A',
    biomarkerTargets: [
      {
        biomarkerId: 'omega3_index',
        biomarkerName: 'Omega-3 Index',
        expectedEffect: 'increase',
        magnitude: 'significant',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'triglycerides',
        biomarkerName: 'Triglycerides',
        expectedEffect: 'decrease',
        magnitude: 'moderate',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
    ],
    knownInteractions: [
      { interactionId: 'INT-OMEGA3-WARFARIN', substanceName: 'Warfarin', severity: 'major', actionRequired: 'block' },
    ],
    tolerableUpperIntake: 3000,
    tolerableUpperIntakeUnit: 'mg',
  },

  // ─────────────────────────────────────────────
  // Ashwagandha
  // ─────────────────────────────────────────────
  {
    ingredientId: 'ING-ASHWAGANDHA',
    commonName: 'Ashwagandha',
    scientificName: 'Withania somnifera',
    aliases: ['Indian ginseng', 'winter cherry', 'withania', 'asgandh'],
    category: 'botanical',
    fdaStatus: 'ndi_notified',
    absoluteContraindications: [
      'hyperthyroidism (uncontrolled)',
      'pregnancy',
    ],
    relativeContraindications: [
      'autoimmune_thyroid_disease (Hashimoto, Graves)',
      'nightshade_sensitivity',
      'autoimmune_conditions (may stimulate immune system)',
    ],
    pregnancySafe: false,
    lactationSafe: false,
    pediatricSafe: false,
    hepaticCaution: true,
    renalCaution: false,
    evidenceGrade: 'B',
    biomarkerTargets: [
      {
        biomarkerId: 'cortisol',
        biomarkerName: 'Serum Cortisol',
        expectedEffect: 'decrease',
        magnitude: 'moderate',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'B',
      },
      {
        biomarkerId: 'tsh',
        biomarkerName: 'Thyroid Stimulating Hormone',
        expectedEffect: 'decrease',
        magnitude: 'minimal',
        timeframe: '8-12 weeks',
        doseDependent: false,
        evidenceGrade: 'B',
      },
      {
        biomarkerId: 'testosterone_total',
        biomarkerName: 'Total Testosterone',
        expectedEffect: 'increase',
        magnitude: 'moderate',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'B',
      },
    ],
    knownInteractions: [
      { interactionId: 'INT-ASHWAGANDHA-THYROID', substanceName: 'Thyroid Medications (levothyroxine)', severity: 'moderate', actionRequired: 'warn_provider' },
      { interactionId: 'INT-ASHWAGANDHA-SEDATIVE', substanceName: 'Sedatives/Benzodiazepines', severity: 'moderate', actionRequired: 'warn_patient' },
    ],
    tolerableUpperIntake: 1200,
    tolerableUpperIntakeUnit: 'mg',
  },

  // ─────────────────────────────────────────────
  // Magnesium Glycinate
  // ─────────────────────────────────────────────
  {
    ingredientId: 'ING-MAGNESIUM-GLYCINATE',
    commonName: 'Magnesium Glycinate',
    scientificName: 'Magnesium bisglycinate',
    aliases: ['magnesium bisglycinate chelate', 'chelated magnesium', 'TRAACS magnesium'],
    category: 'mineral',
    fdaStatus: 'gras',
    absoluteContraindications: [
      'severe_renal_impairment (GFR <30)',
    ],
    relativeContraindications: [
      'myasthenia_gravis',
      'heart_block',
    ],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: true,
    evidenceGrade: 'A',
    biomarkerTargets: [
      {
        biomarkerId: 'magnesium_rbc',
        biomarkerName: 'RBC Magnesium',
        expectedEffect: 'increase',
        magnitude: 'significant',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'magnesium_serum',
        biomarkerName: 'Serum Magnesium',
        expectedEffect: 'increase',
        magnitude: 'moderate',
        timeframe: '4-8 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
    ],
    knownInteractions: [
      { interactionId: 'INT-MAGNESIUM-ANTIBIOTICS', substanceName: 'Fluoroquinolone Antibiotics', severity: 'moderate', actionRequired: 'monitor' },
      { interactionId: 'INT-MAGNESIUM-BISPHOSPHONATE', substanceName: 'Bisphosphonates', severity: 'moderate', actionRequired: 'monitor' },
    ],
    tolerableUpperIntake: 350,
    tolerableUpperIntakeUnit: 'mg',
  },

  // ─────────────────────────────────────────────
  // Berberine
  // ─────────────────────────────────────────────
  {
    ingredientId: 'ING-BERBERINE',
    commonName: 'Berberine',
    scientificName: 'Berberine hydrochloride',
    aliases: ['berberine HCl', 'berberine sulfate', 'berberin'],
    category: 'botanical',
    fdaStatus: 'ndi_notified',
    absoluteContraindications: [
      'pregnancy (uterotonic, teratogenic in animal studies)',
      'lactation (transfer to breast milk, kernicterus risk)',
    ],
    relativeContraindications: [
      'severe_hypoglycemia',
      'concurrent_metformin_high_dose',
      'organ_transplant_on_cyclosporine',
    ],
    pregnancySafe: false,
    lactationSafe: false,
    pediatricSafe: false,
    hepaticCaution: true,
    renalCaution: false,
    evidenceGrade: 'A',
    biomarkerTargets: [
      {
        biomarkerId: 'fasting_glucose',
        biomarkerName: 'Fasting Glucose',
        expectedEffect: 'decrease',
        magnitude: 'significant',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'hba1c',
        biomarkerName: 'Hemoglobin A1C',
        expectedEffect: 'decrease',
        magnitude: 'moderate',
        timeframe: '12-16 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'ldl_cholesterol',
        biomarkerName: 'LDL Cholesterol',
        expectedEffect: 'decrease',
        magnitude: 'moderate',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'B',
      },
      {
        biomarkerId: 'triglycerides',
        biomarkerName: 'Triglycerides',
        expectedEffect: 'decrease',
        magnitude: 'moderate',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'B',
      },
    ],
    knownInteractions: [
      { interactionId: 'INT-BERBERINE-METFORMIN', substanceName: 'Metformin', severity: 'major', actionRequired: 'warn_provider' },
      { interactionId: 'INT-BERBERINE-CYP3A4', substanceName: 'CYP3A4 Substrates', severity: 'major', actionRequired: 'warn_provider' },
    ],
    tolerableUpperIntake: 1500,
    tolerableUpperIntakeUnit: 'mg',
  },

  // ─────────────────────────────────────────────
  // CoQ10
  // ─────────────────────────────────────────────
  {
    ingredientId: 'ING-COQ10',
    commonName: 'Coenzyme Q10 (CoQ10)',
    scientificName: 'Ubiquinol / Ubiquinone',
    aliases: ['ubiquinone', 'ubiquinol', 'coenzyme Q', 'CoQ', 'vitamin Q10'],
    category: 'coenzyme',
    fdaStatus: 'gras',
    absoluteContraindications: [],
    relativeContraindications: [
      'warfarin_therapy (structurally similar to vitamin K)',
    ],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: false,
    evidenceGrade: 'B',
    biomarkerTargets: [
      {
        biomarkerId: 'coq10_serum',
        biomarkerName: 'Serum CoQ10',
        expectedEffect: 'increase',
        magnitude: 'significant',
        timeframe: '4-8 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'blood_pressure_systolic',
        biomarkerName: 'Systolic Blood Pressure',
        expectedEffect: 'decrease',
        magnitude: 'minimal',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'B',
      },
    ],
    knownInteractions: [
      { interactionId: 'INT-COQ10-STATINS', substanceName: 'Statin Medications', severity: 'minor', actionRequired: 'info' },
      { interactionId: 'INT-COQ10-ANTIHYPERTENSIVE', substanceName: 'Antihypertensive Medications', severity: 'moderate', actionRequired: 'monitor' },
    ],
    tolerableUpperIntake: 1200,
    tolerableUpperIntakeUnit: 'mg',
  },

  // ─────────────────────────────────────────────
  // Zinc
  // ─────────────────────────────────────────────
  {
    ingredientId: 'ING-ZINC',
    commonName: 'Zinc',
    scientificName: 'Zinc (as picolinate)',
    aliases: ['zinc picolinate', 'zinc gluconate', 'zinc citrate', 'zinc bisglycinate'],
    category: 'mineral',
    fdaStatus: 'gras',
    absoluteContraindications: [],
    relativeContraindications: [
      'copper_deficiency',
      'concurrent_high_dose_iron (competitive absorption)',
    ],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: false,
    evidenceGrade: 'A',
    biomarkerTargets: [
      {
        biomarkerId: 'zinc_serum',
        biomarkerName: 'Serum Zinc',
        expectedEffect: 'increase',
        magnitude: 'significant',
        timeframe: '4-8 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'testosterone_total',
        biomarkerName: 'Total Testosterone',
        expectedEffect: 'increase',
        magnitude: 'minimal',
        timeframe: '12-24 weeks',
        doseDependent: false,
        evidenceGrade: 'C',
      },
    ],
    knownInteractions: [
      { interactionId: 'INT-ZINC-ANTIBIOTICS', substanceName: 'Fluoroquinolone Antibiotics', severity: 'moderate', actionRequired: 'monitor' },
      { interactionId: 'INT-ZINC-COPPER', substanceName: 'Copper (high-dose zinc depletes)', severity: 'moderate', actionRequired: 'monitor' },
    ],
    tolerableUpperIntake: 40,
    tolerableUpperIntakeUnit: 'mg',
  },

  // ─────────────────────────────────────────────
  // Iron Bisglycinate
  // ─────────────────────────────────────────────
  {
    ingredientId: 'ING-IRON-BISGLYCINATE',
    commonName: 'Iron Bisglycinate',
    scientificName: 'Ferrous bisglycinate chelate',
    aliases: ['Ferrochel', 'chelated iron', 'iron bisglycinate chelate', 'ferrous bisglycinate'],
    category: 'mineral',
    fdaStatus: 'gras',
    absoluteContraindications: [
      'hemochromatosis (hereditary iron overload)',
      'hemolytic_anemia',
      'hemosiderosis',
    ],
    relativeContraindications: [
      'repeated_blood_transfusions',
      'thalassemia',
      'sideroblastic_anemia',
    ],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: true,
    renalCaution: false,
    evidenceGrade: 'A',
    biomarkerTargets: [
      {
        biomarkerId: 'ferritin',
        biomarkerName: 'Serum Ferritin',
        expectedEffect: 'increase',
        magnitude: 'significant',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'hemoglobin',
        biomarkerName: 'Hemoglobin',
        expectedEffect: 'increase',
        magnitude: 'moderate',
        timeframe: '4-8 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'transferrin_saturation',
        biomarkerName: 'Transferrin Saturation',
        expectedEffect: 'increase',
        magnitude: 'moderate',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
    ],
    knownInteractions: [
      { interactionId: 'INT-IRON-THYROID', substanceName: 'Levothyroxine', severity: 'major', actionRequired: 'warn_provider' },
      { interactionId: 'INT-IRON-TETRACYCLINE', substanceName: 'Tetracycline Antibiotics', severity: 'major', actionRequired: 'warn_provider' },
    ],
    tolerableUpperIntake: 45,
    tolerableUpperIntakeUnit: 'mg',
  },

  // ─────────────────────────────────────────────
  // B VITAMINS (used in B-Complex product)
  // ─────────────────────────────────────────────

  {
    ingredientId: 'ING-B1',
    commonName: 'Vitamin B1 (Thiamine)',
    scientificName: 'Thiamine hydrochloride',
    aliases: ['thiamin', 'thiamine HCl', 'aneurine'],
    category: 'vitamin',
    fdaStatus: 'gras',
    absoluteContraindications: [],
    relativeContraindications: [],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: false,
    evidenceGrade: 'A',
    tolerableUpperIntake: undefined,
    tolerableUpperIntakeUnit: 'mg',
  },

  {
    ingredientId: 'ING-B2',
    commonName: 'Vitamin B2 (Riboflavin)',
    scientificName: 'Riboflavin-5-phosphate',
    aliases: ['riboflavin', 'R5P', 'vitamin G', 'lactoflavin'],
    category: 'vitamin',
    fdaStatus: 'gras',
    absoluteContraindications: [],
    relativeContraindications: [],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: false,
    evidenceGrade: 'A',
    tolerableUpperIntake: undefined,
    tolerableUpperIntakeUnit: 'mg',
  },

  {
    ingredientId: 'ING-B3',
    commonName: 'Vitamin B3 (Niacin)',
    scientificName: 'Niacinamide',
    aliases: ['niacin', 'nicotinamide', 'nicotinic acid amide', 'vitamin PP'],
    category: 'vitamin',
    fdaStatus: 'gras',
    absoluteContraindications: [
      'active_liver_disease (niacin form)',
    ],
    relativeContraindications: [
      'gout (niacin form may raise uric acid)',
      'peptic_ulcer (niacin form)',
    ],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: true,
    renalCaution: false,
    evidenceGrade: 'A',
    tolerableUpperIntake: 35,
    tolerableUpperIntakeUnit: 'mg',
  },

  {
    ingredientId: 'ING-B5',
    commonName: 'Vitamin B5 (Pantothenic Acid)',
    scientificName: 'Calcium D-pantothenate',
    aliases: ['pantothenic acid', 'pantothenate', 'calcium pantothenate'],
    category: 'vitamin',
    fdaStatus: 'gras',
    absoluteContraindications: [],
    relativeContraindications: [],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: false,
    evidenceGrade: 'B',
    tolerableUpperIntake: undefined,
    tolerableUpperIntakeUnit: 'mg',
  },

  {
    ingredientId: 'ING-B6',
    commonName: 'Vitamin B6 (Pyridoxine)',
    scientificName: 'Pyridoxal-5-phosphate',
    aliases: ['pyridoxine', 'P5P', 'pyridoxal', 'pyridoxamine'],
    category: 'vitamin',
    fdaStatus: 'gras',
    absoluteContraindications: [],
    relativeContraindications: [
      'levodopa_without_carbidopa (B6 reduces levodopa efficacy)',
    ],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: false,
    evidenceGrade: 'A',
    biomarkerTargets: [
      {
        biomarkerId: 'homocysteine',
        biomarkerName: 'Homocysteine',
        expectedEffect: 'decrease',
        magnitude: 'moderate',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
    ],
    tolerableUpperIntake: 100,
    tolerableUpperIntakeUnit: 'mg',
  },

  {
    ingredientId: 'ING-B7',
    commonName: 'Vitamin B7 (Biotin)',
    scientificName: 'D-Biotin',
    aliases: ['biotin', 'vitamin H', 'coenzyme R'],
    category: 'vitamin',
    fdaStatus: 'gras',
    absoluteContraindications: [],
    relativeContraindications: [
      'pending_lab_work (high-dose biotin interferes with immunoassays: troponin, TSH, fT4)',
    ],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: false,
    evidenceGrade: 'B',
    tolerableUpperIntake: undefined,
    tolerableUpperIntakeUnit: 'mcg',
  },

  {
    ingredientId: 'ING-B9',
    commonName: 'Vitamin B9 (Folate)',
    scientificName: 'L-5-Methyltetrahydrofolate',
    aliases: ['folate', 'folic acid', '5-MTHF', 'methylfolate', 'Quatrefolic'],
    category: 'vitamin',
    fdaStatus: 'gras',
    absoluteContraindications: [],
    relativeContraindications: [
      'undiagnosed_B12_deficiency (folate may mask B12 deficiency neuropathy)',
    ],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: false,
    evidenceGrade: 'A',
    biomarkerTargets: [
      {
        biomarkerId: 'homocysteine',
        biomarkerName: 'Homocysteine',
        expectedEffect: 'decrease',
        magnitude: 'significant',
        timeframe: '4-8 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'folate_serum',
        biomarkerName: 'Serum Folate',
        expectedEffect: 'increase',
        magnitude: 'significant',
        timeframe: '4-8 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
    ],
    tolerableUpperIntake: 1000,
    tolerableUpperIntakeUnit: 'mcg DFE',
  },

  {
    ingredientId: 'ING-B12',
    commonName: 'Vitamin B12 (Cobalamin)',
    scientificName: 'Methylcobalamin',
    aliases: ['cobalamin', 'methylcobalamin', 'cyanocobalamin', 'adenosylcobalamin', 'hydroxocobalamin'],
    category: 'vitamin',
    fdaStatus: 'gras',
    absoluteContraindications: [
      'cobalt_allergy',
    ],
    relativeContraindications: [
      'Leber_hereditary_optic_neuropathy (cyanocobalamin form)',
    ],
    pregnancySafe: true,
    lactationSafe: true,
    pediatricSafe: true,
    hepaticCaution: false,
    renalCaution: false,
    evidenceGrade: 'A',
    biomarkerTargets: [
      {
        biomarkerId: 'vitamin_b12',
        biomarkerName: 'Serum Vitamin B12',
        expectedEffect: 'increase',
        magnitude: 'significant',
        timeframe: '4-8 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'homocysteine',
        biomarkerName: 'Homocysteine',
        expectedEffect: 'decrease',
        magnitude: 'moderate',
        timeframe: '8-12 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
      {
        biomarkerId: 'mma',
        biomarkerName: 'Methylmalonic Acid',
        expectedEffect: 'decrease',
        magnitude: 'significant',
        timeframe: '4-8 weeks',
        doseDependent: true,
        evidenceGrade: 'A',
      },
    ],
    knownInteractions: [
      { interactionId: 'INT-B12-METFORMIN', substanceName: 'Metformin', severity: 'moderate', actionRequired: 'monitor' },
    ],
    tolerableUpperIntake: undefined,
    tolerableUpperIntakeUnit: 'mcg',
  },
];
